import { type Action, type Dispatch, type Store } from "redux"

type BroadcastAction = Action & {
    $tabId?: string
    $isFromBroadcast?: boolean
}

const CHANNEL_NAME = "tab"

// Unique identifier for this tab to avoid processing own messages
const TAB_ID = crypto.randomUUID()

// Flag to mark actions that came from broadcast (to prevent re-broadcasting)
const BROADCAST_SOURCE = "$isFromBroadcast"

const channel = new BroadcastChannel(CHANNEL_NAME)

/**
 * Creates a middleware to broadcast specific Redux actions to other browser tabs
 * Only sync important actions to avoid errors as we don't officially support multiple tabs
 * @param whitelist - Array of action types to broadcast
 */
export const createBroadcastMiddleware = (whitelist: Array<Action["type"]>) => (_store: Store) => (next: Dispatch<Action>) => (action: BroadcastAction) => {
    if (whitelist.includes(action.type) && !action[BROADCAST_SOURCE]) {
        channel.postMessage({ ...action, $tabId: TAB_ID })
    }
    next(action)
}

/**
 * Initialize the broadcast channel listener to receive actions from other tabs
 * Must be called after the store is created
 */
export const initBroadcastListener = (store: Store) => {
    channel.addEventListener("message", (event) => {
        const action = event.data
        if (action.$tabId === TAB_ID) {
            return
        }
        // Mark the action as coming from broadcast to prevent re-broadcasting
        store.dispatch({ ...action, [BROADCAST_SOURCE]: true })
    })
}
